import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bot,
  BookOpen,
  KeyRound,
  Link2,
  MoreHorizontal,
  Pause,
  Phone,
  PhoneCall,
  Play,
  Sparkles,
  Timer,
  Trash2,
  Webhook,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  customersApi,
  type CustomerDetail,
  type CustomerRef,
} from "@/features/customers/api";
import {
  AddMinutesDialog,
  AssignDialog,
  CUSTOMERS_KEY,
  DeleteDialog,
  ResetPasswordDialog,
  errText,
} from "@/features/customers/dialogs";
import { planEconomics } from "@/features/customers/economics";
import { money, onDay, perMinute } from "@/features/customers/format";
import {
  MinutesBar,
  fmtMinutes,
  minuteLevel,
  minuteTextClass,
} from "@/features/customers/minutes";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

/** One customer, everything about them, on one page.
 *
 * The tenant's counterpart to the customer's own My Plan page: what they were
 * sold, what they have used, what they have built, and what they cost. It exists
 * because the answers were scattered — minutes on the Customers row, agents on
 * the Agents page filtered by nothing, calls nowhere.
 */
export default function CustomerDetailPage() {
  const { id = "" } = useParams();
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const [assignOpen, setAssignOpen] = useState(false);
  const [minutesOpen, setMinutesOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const q = useQuery({
    queryKey: [...CUSTOMERS_KEY, id],
    queryFn: () => customersApi.get(id),
    enabled: !!id,
  });
  const callsQ = useQuery({
    queryKey: [...CUSTOMERS_KEY, id, "calls"],
    queryFn: () => customersApi.calls(id),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [...CUSTOMERS_KEY] });
  };

  if (!hasMinRole(me?.role, "admin")) return <Navigate to="/dashboard" replace />;
  if (deleted) return <Navigate to="/customers" replace />;

  if (q.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="p-8 text-center text-sm text-muted-foreground">
          This customer could not be loaded. They may have been deleted.
        </p>
      </div>
    );
  }

  const c = q.data;
  const u = c.usage;
  const level = minuteLevel(u);

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{c.name}</h1>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                c.status === "active"
                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                  : "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
              )}
            >
              {c.status === "active" ? "Active" : "Paused"}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {c.email} · customer since {onDay(c.created_at)}
            {c.login?.last_login
              ? ` · last signed in ${onDay(c.login.last_login)}`
              : " · has never signed in"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setMinutesOpen(true)}>
            <Timer className="mr-2 h-4 w-4" /> Add minutes
          </Button>
          <Button variant="outline" onClick={() => setAssignOpen(true)}>
            <Link2 className="mr-2 h-4 w-4" /> Assign
          </Button>
          <ActionsMenu
            customer={c}
            onPassword={() => setPwOpen(true)}
            onDelete={() => setDeleteOpen(true)}
            onChanged={invalidate}
          />
        </div>
      </div>

      {u.blocked && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-medium text-destructive">
            Their calls are being refused — no minutes left
          </p>
          <p className="mt-0.5 text-muted-foreground">
            Add minutes to start them again, or they wait until{" "}
            {onDay(u.period_end)}.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Minutes */}
        <Card className="lg:col-span-2">
          <CardContent className="space-y-3 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Minutes this period
                </p>
                <p className="mt-0.5 text-2xl font-semibold tabular-nums">
                  {fmtMinutes(u.minutes_used)}
                  <span className="text-sm font-normal text-muted-foreground">
                    {" "}
                    of {fmtMinutes(u.minutes_allowed)}
                  </span>
                </p>
              </div>
              <p className={cn("text-sm tabular-nums", minuteTextClass(level))}>
                {u.metered
                  ? `${fmtMinutes(u.minutes_left)} left · ${u.percent_used}% used`
                  : "no plan · not limited"}
              </p>
            </div>
            {u.metered && <MinutesBar usage={u} className="h-2" />}
            <p className="text-xs text-muted-foreground">
              {onDay(u.period_start)} – {onDay(u.period_end)}
              {u.extra_minutes > 0 &&
                ` · includes ${fmtMinutes(u.extra_minutes)} you added`}
            </p>
          </CardContent>
        </Card>

        {/* What they pay, what they cost */}
        <Card>
          <CardContent className="space-y-2 p-5 text-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              This customer
            </p>
            {c.plan ? (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">Pays you</span>
                  <span className="font-medium">
                    {money(c.plan.price_cents)}/mo
                  </span>
                </div>
                <Economics customer={c} />
              </>
            ) : (
              <p className="text-muted-foreground">
                Not on a plan.{" "}
                <Link to="/customer-plans" className="underline underline-offset-2">
                  Create one
                </Link>{" "}
                and set it on this customer so their minutes are counted.
              </p>
            )}
            {c.billing.next_charge_at && (
              <p className="border-t pt-2 text-xs text-muted-foreground">
                Your next {money(c.billing.fee_cents)} charge for them:{" "}
                {onDay(c.billing.next_charge_at)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* What they own */}
      <Card>
        <CardContent className="p-5">
          <p className="text-sm font-medium">What they have</p>
          <p className="text-xs text-muted-foreground">
            Assigned by you, or built by them in their own dashboard.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ResourceList
              icon={Phone}
              label="Phone numbers"
              rows={c.resources.phone_numbers}
              empty="None assigned"
            />
            <ResourceList
              icon={Sparkles}
              label="Assistants"
              rows={c.resources.assistants}
              empty="None yet"
            />
            <ResourceList
              icon={Bot}
              label="Agents"
              rows={c.resources.agents}
              empty="None yet"
            />
            <ResourceList
              icon={BookOpen}
              label="Knowledge bases"
              rows={c.resources.knowledge_bases}
              empty="None yet"
            />
            <ResourceList
              icon={Workflow}
              label="Flows"
              rows={c.resources.flows}
              empty="None yet"
            />
            <ResourceList
              icon={Webhook}
              label="Webhooks"
              rows={c.resources.webhooks}
              empty="None yet"
            />
          </div>
        </CardContent>
      </Card>

      {/* Their calls */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-baseline justify-between gap-4 border-b px-5 py-4">
            <div>
              <p className="text-sm font-medium">Recent calls</p>
              <p className="text-xs text-muted-foreground">
                Every call their minutes were charged for — on their numbers, or
                from their own dashboard.
              </p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/calls">All calls</Link>
            </Button>
          </div>
          {callsQ.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (callsQ.data?.length ?? 0) === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              No calls yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-2.5 font-medium">When</th>
                  <th className="px-5 py-2.5 font-medium">From</th>
                  <th className="px-5 py-2.5 font-medium">Answered by</th>
                  <th className="px-5 py-2.5 font-medium">Status</th>
                  <th className="px-5 py-2.5 text-right font-medium">Minutes</th>
                </tr>
              </thead>
              <tbody>
                {callsQ.data!.map((call) => (
                  <tr key={call.id} className="border-b last:border-0">
                    <td className="whitespace-nowrap px-5 py-2.5 text-muted-foreground">
                      {call.started_at ? onDay(call.started_at) : "—"}
                    </td>
                    <td className="px-5 py-2.5">
                      {call.from_number || "—"}
                      {call.on_number && (
                        <span className="text-xs text-muted-foreground">
                          {" → "}
                          {call.on_number}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="inline-flex items-center gap-1.5">
                        <PhoneCall className="h-3.5 w-3.5 text-muted-foreground" />
                        {call.answered_by || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-muted-foreground">
                      {call.status}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums">
                      {call.minutes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {c.notes && (
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium">Your notes</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              {c.notes}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Never shown to the customer.
            </p>
          </CardContent>
        </Card>
      )}

      {assignOpen && (
        <AssignDialog
          customer={c}
          onClose={() => setAssignOpen(false)}
          onChanged={invalidate}
        />
      )}
      {minutesOpen && (
        <AddMinutesDialog
          customer={c}
          onClose={() => setMinutesOpen(false)}
          onChanged={invalidate}
        />
      )}
      {pwOpen && <ResetPasswordDialog customer={c} onClose={() => setPwOpen(false)} />}
      {deleteOpen && (
        <DeleteDialog
          customer={c}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => {
            invalidate();
            setDeleted(true);
          }}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/customers"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> Customers
    </Link>
  );
}

/** What this one customer earns the tenant, from the shared arithmetic.
 *
 * Same helper as the plan dialog and the add-customer dialog, so the number a
 * tenant sees here can't disagree with the one that sold them the plan. Uses
 * minutes actually used, not the whole allowance — this is the month so far.
 */
function Economics({ customer }: { customer: CustomerDetail }) {
  const plan = customer.plan!;
  const { fee_cents, per_minute_cents } = customer.billing;
  const usedCost = customer.usage.minutes_used * per_minute_cents;
  const soFar = plan.price_cents - fee_cents - usedCost;
  const full = planEconomics({
    priceCents: plan.price_cents,
    includedMinutes: plan.included_minutes,
    feeCents: fee_cents,
    costPerMinuteCents: per_minute_cents,
  });
  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground">Costs you so far</span>
        <span className="font-medium">{money(fee_cents + usedCost)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground">Profit so far</span>
        <span
          className={cn(
            "font-medium",
            soFar >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive",
          )}
        >
          {money(soFar)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {money(fee_cents)} for having them + {fmtMinutes(customer.usage.minutes_used)}{" "}
        min × {perMinute(per_minute_cents)}. If they use every included minute you
        keep {money(full.profit)}.
      </p>
    </>
  );
}

function ResourceList({
  icon: Icon,
  label,
  rows,
  empty,
}: {
  icon: typeof Bot;
  label: string;
  rows: CustomerRef[];
  empty: string;
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {label}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {rows.length}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="mt-1.5 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {rows.slice(0, 6).map((r) => (
            <li key={r.id} className="truncate text-xs text-muted-foreground">
              {r.name}
            </li>
          ))}
          {rows.length > 6 && (
            <li className="text-xs text-muted-foreground">
              + {rows.length - 6} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ActionsMenu({
  customer,
  onPassword,
  onDelete,
  onChanged,
}: {
  customer: CustomerDetail;
  onPassword: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const toggle = useMutation({
    mutationFn: () =>
      customersApi.update(customer.id, {
        status: customer.status === "active" ? "paused" : "active",
      }),
    onSuccess: () => {
      toast.success(
        customer.status === "active"
          ? "Customer paused — their monthly charge stops."
          : "Customer activated.",
      );
      onChanged();
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => toggle.mutate()}>
          {customer.status === "active" ? (
            <>
              <Pause className="mr-2 h-4 w-4" /> Pause
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" /> Activate
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onPassword}>
          <KeyRound className="mr-2 h-4 w-4" /> Reset password
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onDelete}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
